import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, memo } from 'react'
import { House, CalendarBlank, QrCode, Scan, ChartBar, User } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { canUserScan } from '../utils/permissions'

function SubgroupLeaderBottomNav() {
  const location = useLocation()
  const [canScan, setCanScan] = useState(() => {
    // Initialize from cache immediately
    const cached = localStorage.getItem('leaderCanScan')
    return cached === 'true'
  })
  const [checkingPermission, setCheckingPermission] = useState(false)
  const [userId, setUserId] = useState(null)

  useEffect(() => {
    checkScanPermission()
  }, [])

  const checkScanPermission = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        localStorage.removeItem('leaderCanScan')
        return
      }

      setUserId(user.id)

      // Don't show loading if we have cached value
      const cached = localStorage.getItem('leaderCanScan')
      if (!cached) {
        setCheckingPermission(true)
      }

      // Check if user's team has scan permission
      const hasScanPermission = await canUserScan(user.id)
      setCanScan(hasScanPermission)
      localStorage.setItem('leaderCanScan', hasScanPermission.toString())
    } catch (error) {
      console.error('Error checking scan permission:', error)
      setCanScan(false)
      localStorage.removeItem('leaderCanScan')
    } finally {
      setCheckingPermission(false)
    }
  }

  const isActive = (path) => location.pathname === path

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg safe-area-bottom z-50">
      <div className="grid grid-cols-5 h-16">
        {/* Home */}
        <Link
          to="/subgroup-leader/home"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/subgroup-leader/home')
              ? 'text-blue-600 border-t-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <House size={24} weight={isActive('/subgroup-leader/home') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/subgroup-leader/home') ? 'font-bold' : ''}`}>Home</span>
        </Link>

        {/* Events */}
        <Link
          to="/subgroup-leader/events"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/subgroup-leader/events')
              ? 'text-blue-600 border-t-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarBlank size={24} weight={isActive('/subgroup-leader/events') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/subgroup-leader/events') ? 'font-bold' : ''}`}>Event</span>
        </Link>

        {/* Scanner OR Code (conditional based on scan permission) */}
        {checkingPermission ? (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <QrCode size={24} weight="light" className="opacity-50" />
            <span className="text-xs mt-1">...</span>
          </div>
        ) : canScan ? (
          <Link
            to="/subgroup-leader/scanner"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/subgroup-leader/scanner')
                ? 'text-blue-600 border-t-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Scan size={24} weight={isActive('/subgroup-leader/scanner') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/subgroup-leader/scanner') ? 'font-bold' : ''}`}>Scanner</span>
          </Link>
        ) : (
          <Link
            to="/subgroup-leader/code"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/subgroup-leader/code')
                ? 'text-blue-600 border-t-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <QrCode size={24} weight={isActive('/subgroup-leader/code') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/subgroup-leader/code') ? 'font-bold' : ''}`}>Code</span>
          </Link>
        )}

        {/* Stats */}
        <Link
          to="/subgroup-leader/stats"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/subgroup-leader/stats')
              ? 'text-blue-600 border-t-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ChartBar size={24} weight={isActive('/subgroup-leader/stats') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/subgroup-leader/stats') ? 'font-bold' : ''}`}>Stat</span>
        </Link>

        {/* Profile */}
        <Link
          to="/subgroup-leader/profile"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/subgroup-leader/profile')
              ? 'text-blue-600 border-t-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User size={24} weight={isActive('/subgroup-leader/profile') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/subgroup-leader/profile') ? 'font-bold' : ''}`}>Profile</span>
        </Link>
      </div>
    </nav>
  )
}

export default memo(SubgroupLeaderBottomNav)